import React, { useMemo } from 'react';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import { compareProfilesByScore, Profile } from 'features/profiles/useProfiles';
import { badgeClaimPercent, formatBadgeClaimPercent } from 'features/claim/badge-claim-eligibility.ts';
import { isBadgeReleased } from 'features/catalog/badge-release.ts';
import { contributorBadgeCount, getContributorIds } from 'features/catalog/badge-contributors.ts';
import { BADGE_GROUPS, slugifyGroupName, sortBadgeGroupsByGlobalClaimRate } from 'features/catalog/badge-groups.ts';
import { badgeIdsInJourney, JOURNEYS, isJourneyCompleted } from 'features/catalog/badge-journeys.ts';
import { getRankDisplay } from 'config/constants.ts';
import { getFirstPartOfEmail } from 'shared/lib/email.ts';
import {
  compareBadgesByScoreDesc,
  countJourneyCompletionsById,
  getRarityImageUrlByName,
  rarityOrderIndex,
} from 'features/catalog/rarity';
import { BadgeThumb } from 'shared/components/BadgeThumb';
import { PageProfileSearch } from 'features/profiles/PageProfileSearch';
import { ParticipantCard } from 'features/profiles/ParticipantCard.tsx';
import { StatsCharts } from 'features/stats/StatsCharts';
import './StatsComponent.css';
import { ContributorInfo, GroupWinnerInfo } from 'shared/components/types.ts';

export type StatsProfileTarget =
  { section: 'collected'; groupSlug?: string } | { section: 'submitted' } | { section: 'journeys' };

interface StatsComponentProps {
  onOpenProfile: (profileId: string, target?: StatsProfileTarget) => void;
  setSelectedBadge: (badge: Badge | null) => void;
}

interface CompareState {
  profile1: Profile | null;
  profile2: Profile | null;
}

type BadgeStats = {
  mostCommon: Badge | null;
  total: number;
  badgeIDs: string[];
  kings: {
    profile: Profile;
    count: number;
  }[];
};

export const StatsComponent: React.FC<StatsComponentProps> = ({ onOpenProfile, setSelectedBadge }) => {
  const { badgesMap, profilesMap, rankedProfiles, contributorIndex } = useTalosData();
  const [compareState, setCompareState] = React.useState<CompareState>({
    profile1: null,
    profile2: null,
  });
  const totalProfiles = profilesMap.size;
  const badgeStats = useMemo(() => {
    const statsByRarity: Record<string, BadgeStats> = {
      common: { mostCommon: null, total: 0, badgeIDs: [], kings: [] },
      rare: { mostCommon: null, total: 0, badgeIDs: [], kings: [] },
      legendary: { mostCommon: null, total: 0, badgeIDs: [], kings: [] },
      mythic: { mostCommon: null, total: 0, badgeIDs: [], kings: [] },
    };

    const badgesByRarity: Record<string, Badge[]> = {
      common: [],
      rare: [],
      legendary: [],
      mythic: [],
    };

    badgesMap.forEach((badge) => {
      if (badgesByRarity[badge.rarityName]) {
        badgesByRarity[badge.rarityName].push(badge);
      }
    });

    for (const rarity in badgesByRarity) {
      const badges = badgesByRarity[rarity];
      if (badges.length === 0) {
        continue;
      }

      let mostCommon = badges[0];
      for (const badge of badges) {
        if ((badge.badgeScore ?? 0) > (mostCommon.badgeScore ?? 0)) {
          mostCommon = badge;
        }
      }

      const rarityBadgeIDs = new Set(badges.map((b) => b.id));
      const collectors: { profile: Profile; count: number }[] = [];
      profilesMap.forEach((profile) => {
        const count = profile.badgesIDs.filter((id) => rarityBadgeIDs.has(id)).length;
        if (count > 0) {
          collectors.push({ profile, count });
        }
      });

      collectors.sort((a, b) => {
        if (a.count !== b.count) {
          return b.count - a.count;
        }
        // Same rarity count → higher overall score wins (then earlier lastUpdated).
        return compareProfilesByScore(a.profile, b.profile);
      });

      statsByRarity[rarity] = {
        mostCommon,
        total: rarityBadgeIDs.size,
        badgeIDs: [...rarityBadgeIDs],
        kings: collectors.slice(0, 3),
      };
    }

    return statsByRarity;
  }, [badgesMap, profilesMap]);

  const { journeyStats, journeyLeaders } = useMemo(() => {
    const completionsByJourney = countJourneyCompletionsById(profilesMap.values());
    const profileJourneyCounts: { profile: Profile; count: number }[] = [];

    profilesMap.forEach((profile) => {
      const owned = new Set(profile.badgesIDs);
      let completedJourneys = 0;
      JOURNEYS.forEach((journey) => {
        if (!isJourneyCompleted(journey, owned)) return;
        completedJourneys++;
      });
      profileJourneyCounts.push({ profile, count: completedJourneys });
    });

    const stats = JOURNEYS.map((journey) => ({
      ...journey,
      completions: completionsByJourney.get(journey.id) ?? 0,
    })).sort((a, b) => {
      if (b.completions !== a.completions) return b.completions - a.completions;
      return a.name.localeCompare(b.name);
    });
    const leaders = profileJourneyCounts
      .sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        return compareProfilesByScore(a.profile, b.profile);
      })
      .slice(0, 4);

    return { journeyStats: stats, journeyLeaders: leaders };
  }, [profilesMap]);

  const journeyBadgesById = useMemo(() => {
    const allBadges = Array.from(badgesMap.values());
    return new Map(
      JOURNEYS.map((journey) => {
        const badgeIDs = new Set(badgeIdsInJourney(journey));
        return [journey.id, allBadges.filter((badge) => badgeIDs.has(badge.id))] as const;
      })
    );
  }, [badgesMap]);

  const groupCompletion = useMemo(() => {
    const profiles = [...profilesMap.values()];
    const n = profiles.length;
    return BADGE_GROUPS.map((group) => {
      let sum = 0;
      for (const profile of profiles) {
        const owned = profile.badgesIDs.filter((id) => group.badgesIDs.includes(id)).length;
        sum += group.badgesIDs.length === 0 ? 0 : owned / group.badgesIDs.length;
      }
      return {
        name: group.name,
        slug: slugifyGroupName(group.name),
        pct: n === 0 ? 0 : Math.round((sum / n) * 1000) / 10,
        colors: group.colors,
        badges: group.badgesIDs.map((id) => badgesMap.get(id)).filter((badge): badge is Badge => Boolean(badge)),
      };
    }).sort((a, b) => b.pct - a.pct);
  }, [badgesMap, profilesMap]);

  const badgeContributors = useMemo((): ContributorInfo[] => {
    return Array.from(profilesMap.entries())
      .map(([email, profile]) => {
        const ids = getContributorIds(contributorIndex, email);
        const badgeTooltipIDs = [...new Set([...ids.creditIds, ...ids.designIds])].sort((a, b) => {
          const aBadge = badgesMap.get(a);
          const bBadge = badgesMap.get(b);
          if (aBadge == null || bBadge == null) return 0;
          return (aBadge.badgeScore ?? 0) - (bBadge.badgeScore ?? 0);
        });
        return {
          email,
          name: profile.name,
          credits: ids.creditIds.length,
          designs: ids.designIds.length,
          total: contributorBadgeCount(ids),
          badgeTooltipIDs,
        };
      })
      .filter((c) => c.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [badgesMap, profilesMap, contributorIndex]);

  const topBadgesClaimed = useMemo(() => {
    const profileBadgeCounts: { profile: Profile; count: number }[] = [];

    profilesMap.forEach((profile) => {
      profileBadgeCounts.push({ profile, count: profile.badgesIDs.length });
    });

    return profileBadgeCounts
      .sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        return a.profile.lastUpdated.getTime() - b.profile.lastUpdated.getTime();
      })
      .slice(0, 4);
  }, [profilesMap]);

  const topCumulativeScore = useMemo(
    () => rankedProfiles.slice(0, 4).map((profile) => ({ profile, score: profile.sortScore })),
    [rankedProfiles]
  );

  const groupWinners = useMemo((): GroupWinnerInfo[] => {
    return sortBadgeGroupsByGlobalClaimRate(BADGE_GROUPS, badgesMap, profilesMap.size).map((group) => {
      const badgeTooltipIDs = new Set(group.badgesIDs);

      // Find all participants and their progress for this group
      const candidates: { profile: Profile; points: number }[] = [];
      profilesMap.forEach((profile) => {
        const points = profile.badgesIDs
          .filter((id) => badgeTooltipIDs.has(id))
          .reduce((sum, badgeID) => {
            const badge = badgesMap.get(badgeID);
            return sum + (badge?.rarityScore || 0);
          }, 0);
        if (points > 0) {
          candidates.push({ profile, points });
        }
      });

      if (candidates.length === 0) {
        return { groupName: group.name, winners: [], colors: group.colors };
      }

      // Sort by points (descending) and then by completion time (ascending)
      candidates.sort((a, b) => {
        if (a.points !== b.points) {
          return b.points - a.points;
        }
        return compareProfilesByScore(a.profile, b.profile);
      });

      const topCandidates = candidates.slice(0, 3);
      return {
        groupName: group.name,
        winners: topCandidates.map((c) => c.profile),
        colors: group.colors,
      };
    });
  }, [profilesMap, badgesMap]);

  const handleProfileClick = (email: string, target: StatsProfileTarget = { section: 'collected' }) => {
    onOpenProfile(getFirstPartOfEmail(email), target);
  };

  const handleCompareProfileSelect = (profile: Profile, slot: 1 | 2) => {
    setCompareState((prev) => ({
      ...prev,
      [`profile${slot}`]: profile,
    }));
  };

  const clearCompareSelection = (slot: 1 | 2) => {
    setCompareState((prev) => ({
      ...prev,
      [`profile${slot}`]: null,
    }));
  };

  const renderBadgeStat = (badge: Badge | null, label: string) => {
    if (!badge) return <p>{label}: N/A</p>;
    return (
      <p>
        {label}: {badge.name} ({formatBadgeClaimPercent(badge)})
      </p>
    );
  };

  const badgesSortedByClaimedPercentage = useMemo(() => {
    return Array.from(badgesMap.values())
      .filter((badge) => isBadgeReleased(badge.id))
      .sort((a, b) => {
        const pctDiff = badgeClaimPercent(b) - badgeClaimPercent(a);
        if (pctDiff !== 0) return pctDiff;
        // Tie-break on Claimed-By count (same number the modal shows), not the
        // eligibility-sample numerator used only for claim %.
        const ownersDiff =
          (b.participantIDsWhoOwnThisBadge?.length ?? 0) - (a.participantIDsWhoOwnThisBadge?.length ?? 0);
        if (ownersDiff !== 0) return ownersDiff;
        return a.name.localeCompare(b.name);
      });
  }, [badgesMap]);

  const badgesUsedInMultipleJourneys = useMemo(() => {
    const badgeJourneyCount = new Map<string, number>();

    // Count how many journeys each badge appears in
    JOURNEYS.forEach((journey) => {
      const journeyBadgeIDs = badgeIdsInJourney(journey);
      journeyBadgeIDs.forEach((badgeID) => {
        badgeJourneyCount.set(badgeID, (badgeJourneyCount.get(badgeID) || 0) + 1);
      });
    });

    // Get badges that appear in multiple journeys
    return Array.from(badgesMap.values())
      .filter((badge) => (badgeJourneyCount.get(badge.id) || 0) > 1)
      .map((badge) => ({
        badge,
        journeyCount: badgeJourneyCount.get(badge.id) || 0,
      }))
      .sort((a, b) => b.journeyCount - a.journeyCount);
  }, [badgesMap]);

  const badgesNotInAnyJourney = useMemo(() => {
    const journeyBadgeIDs = new Set<string>();

    // Collect all badge IDs used in journeys
    JOURNEYS.forEach((journey) => {
      const journeyBadgeIDsList = badgeIdsInJourney(journey);
      journeyBadgeIDsList.forEach((badgeID) => journeyBadgeIDs.add(badgeID));
    });

    // Get badges that are not in any journey and have at least 1 claim
    return Array.from(badgesMap.values())
      .filter((badge) => !journeyBadgeIDs.has(badge.id) && badge.participantIDsWhoOwnThisBadge.length > 0)
      .sort(compareBadgesByScoreDesc);
  }, [badgesMap]);

  const comparisonData = useMemo(() => {
    if (!compareState.profile1 || !compareState.profile2) return null;

    const allBadges = Array.from(badgesMap.values());
    const comparison = allBadges.map((badge) => {
      const profile1Has = compareState.profile1!.badgesIDs.includes(badge.id);
      const profile2Has = compareState.profile2!.badgesIDs.includes(badge.id);

      const status: 'both' | 'profile1' | 'profile2' | 'neither' =
        profile1Has && profile2Has ? 'both' : profile1Has ? 'profile1' : profile2Has ? 'profile2' : 'neither';

      return {
        badge,
        profile1Has,
        profile2Has,
        status,
      };
    });

    return {
      profile1: compareState.profile1,
      profile2: compareState.profile2,
      comparison,
      stats: {
        profile1Total: compareState.profile1.badgesIDs.length,
        profile2Total: compareState.profile2.badgesIDs.length,
        shared: comparison.filter((c) => c.status === 'both').length,
        profile1Only: comparison.filter((c) => c.status === 'profile1').length,
        profile2Only: comparison.filter((c) => c.status === 'profile2').length,
      },
    };
  }, [compareState, badgesMap]);

  return (
    <div className="stats-container" onClick={(e) => e.stopPropagation()}>
      <div className="stat-card">
        <h2>Badge Stats</h2>
        <div className="rarity-stats-grid">
          {Object.entries(badgeStats)
            .sort(([rarityA], [rarityB]) => rarityOrderIndex(rarityA) - rarityOrderIndex(rarityB))
            .map(([rarity, stats]) => (
              <div key={rarity} className={`rarity-stats rarity-background-${rarity}`}>
                <h3>{rarity.charAt(0).toUpperCase() + rarity.slice(1)}</h3>
                <div className="common-stats-extra">
                  {renderBadgeStat(stats.mostCommon, 'Most Claimed')}
                  <p>Total: {stats.total} badges</p>
                  {stats.kings.length > 0 && (
                    <>
                      <p>Most {rarity} badges:</p>
                      {stats.kings.map((king, index) => (
                        <ParticipantCard
                          key={king.profile.email}
                          profile={king.profile}
                          badgesMap={badgesMap}
                          onClick={() =>
                            handleProfileClick(king.profile.email, {
                              section: 'collected',
                            })
                          }
                          subtext={`${getRankDisplay(index + 1)} with ${king.count}/${stats.total} ${rarity} badges`}
                          variant="stats"
                          showBadgeTooltip={true}
                          // All the ids of that rarity
                          badgeTooltipIDs={stats.badgeIDs}
                          onBadgeClick={setSelectedBadge}
                        />
                      ))}
                    </>
                  )}
                </div>
              </div>
            ))}

          <div className="rarity-stats">
            <h3>Top Badge Contributors</h3>
            <div className="common-stats-extra">
              {badgeContributors.slice(0, 4).map((contributor, index) => {
                const profile = profilesMap.get(contributor.email);
                if (!profile) return null;
                return (
                  <ParticipantCard
                    key={contributor.email}
                    profile={profile}
                    badgesMap={badgesMap}
                    onClick={() =>
                      handleProfileClick(contributor.email, {
                        section: 'submitted',
                      })
                    }
                    subtext={`${getRankDisplay(index + 1)} with contributions to ${contributor.total} badges (${contributor.credits}✏️ / ${contributor.designs}🎨)`}
                    variant="stats"
                    showBadgeTooltip={true}
                    showUnclaimedBadges={true}
                    badgeTooltipIDs={contributor.badgeTooltipIDs}
                    onBadgeClick={setSelectedBadge}
                  />
                );
              })}
            </div>
          </div>

          <div className="rarity-stats" style={{ display: 'none' }}>
            <h3>Highest Cumulative Score</h3>
            <div className="common-stats-extra">
              {topCumulativeScore.map((leader, index) => (
                <ParticipantCard
                  key={leader.profile.email}
                  profile={leader.profile}
                  badgesMap={badgesMap}
                  onClick={() =>
                    handleProfileClick(leader.profile.email, {
                      section: 'collected',
                    })
                  }
                  subtext={`${getRankDisplay(index + 1)} with ${leader.score} points`}
                  variant="stats"
                />
              ))}
            </div>
          </div>

          <div className="rarity-stats" style={{ display: 'none' }}>
            <h3>Most Badges Claimed</h3>
            <div className="common-stats-extra">
              {topBadgesClaimed.map((leader, index) => (
                <ParticipantCard
                  key={leader.profile.email}
                  profile={leader.profile}
                  badgesMap={badgesMap}
                  onClick={() =>
                    handleProfileClick(leader.profile.email, {
                      section: 'collected',
                    })
                  }
                  subtext={`${getRankDisplay(index + 1)} with ${leader.count}/${badgesMap.size} badges`}
                  variant="stats"
                />
              ))}
            </div>
          </div>

          <div className="rarity-stats">
            <h3>Most Journeys Completed</h3>
            <div className="common-stats-extra">
              {journeyLeaders.map((leader, index) => (
                <ParticipantCard
                  key={leader.profile.email}
                  profile={leader.profile}
                  badgesMap={badgesMap}
                  onClick={() =>
                    handleProfileClick(leader.profile.email, {
                      section: 'journeys',
                    })
                  }
                  variant="stats"
                  subtext={`with ${leader.count}/${JOURNEYS.length} ${leader.profile.journeyEmojis
                    .map((emoji) => emoji)
                    .join(' ')} `}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <StatsCharts />

      <div className="stat-card">
        <h2>Journey Completions</h2>
        <div className="most-claimed-badges journey-completions">
          {journeyStats.map((journey) => {
            const journeyBadges = journeyBadgesById.get(journey.id) ?? [];

            return (
              <div key={journey.id} className="journey-stat-item journey-name-with-tooltip">
                <div className="journey-tooltip">
                  <div className="journey-badges-grid">
                    {journeyBadges.map((badge) => (
                      <button
                        key={badge.id}
                        type="button"
                        className="journey-badge-tooltip-btn"
                        title={badge.name}
                        aria-label={badge.name}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBadge(badge);
                        }}
                      >
                        <BadgeThumb
                          badge={badge}
                          size="md"
                          alt={badge.name}
                          imageClassName="journey-badge-tooltip-image"
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="journey-info">
                  <span>
                    {journey.emoji} {journey.name}
                  </span>
                  <span>
                    {journey.completions} / {totalProfiles}
                  </span>
                </div>
                <div className="bar-container">
                  <div
                    className={`bar rarity-${journey.rarity}`}
                    style={{
                      width: `${totalProfiles > 0 ? (journey.completions / totalProfiles) * 100 : 0}%`,
                    }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="stat-card">
        <h2>Average group completion</h2>
        <div className="most-claimed-badges journey-completions">
          {groupCompletion.map((group) => (
            <div key={group.slug} className="journey-stat-item journey-name-with-tooltip">
              <div className="journey-tooltip">
                <div className="journey-badges-grid">
                  {group.badges.map((badge) => (
                    <button
                      key={badge.id}
                      type="button"
                      className="journey-badge-tooltip-btn"
                      title={badge.name}
                      aria-label={badge.name}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBadge(badge);
                      }}
                    >
                      <BadgeThumb
                        badge={badge}
                        size="md"
                        alt={badge.name}
                        imageClassName="journey-badge-tooltip-image"
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div className="journey-info">
                <span>{group.name}</span>
                <span>{group.pct}%</span>
              </div>
              <div className="bar-container">
                <div
                  className="bar"
                  style={{
                    width: `${group.pct}%`,
                    background: `linear-gradient(90deg, ${group.colors[0]}, ${group.colors[1]})`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="stat-card">
        <h2>Compare Profiles</h2>
        <div className="compare-section">
          <div className="profile-selectors">
            <div className="profile-selector">
              <h3>Profile 1</h3>
              <PageProfileSearch
                className="compare-profile-search"
                selectedProfileId={compareState.profile1 ? getFirstPartOfEmail(compareState.profile1.email) : null}
                onSelectProfile={(id) => {
                  const profile = profilesMap.get(id);
                  if (profile) handleCompareProfileSelect(profile, 1);
                }}
                onClear={() => clearCompareSelection(1)}
                placeholder="Select Profile"
              />
            </div>

            <div className="profile-selector">
              <h3>Profile 2</h3>
              <PageProfileSearch
                className="compare-profile-search"
                selectedProfileId={compareState.profile2 ? getFirstPartOfEmail(compareState.profile2.email) : null}
                onSelectProfile={(id) => {
                  const profile = profilesMap.get(id);
                  if (profile) handleCompareProfileSelect(profile, 2);
                }}
                onClear={() => clearCompareSelection(2)}
                placeholder="Select Profile"
              />
            </div>
          </div>

          {comparisonData && (
            <div className="comparison-results">
              <div className="comparison-stats">
                <div className="stat-item">
                  <span className="stat-label">{comparisonData.profile1.name} only:</span>
                  <span className="stat-value profile1-only">{comparisonData.stats.profile1Only}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Shared Badges:</span>
                  <span className="stat-value shared">{comparisonData.stats.shared}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">{comparisonData.profile2.name} only:</span>
                  <span className="stat-value profile2-only">{comparisonData.stats.profile2Only}</span>
                </div>
              </div>

              <div className="comparison-chart">
                <h3>Badge Comparison</h3>
                <div className="badge-comparison-grid">
                  {comparisonData.comparison
                    .filter((item) => item.status !== 'neither')
                    .sort((a, b) => {
                      // Sort by status: profile1 only, then profile2 only, then shared
                      const statusOrder: Record<'profile1' | 'profile2' | 'both' | 'neither', number> = {
                        profile1: 0,
                        profile2: 1,
                        both: 2,
                        neither: 3,
                      };
                      return statusOrder[a.status] - statusOrder[b.status];
                    })
                    .map((item) => (
                      <div
                        key={item.badge.id}
                        className={`comparison-badge-item ${item.status}`}
                        onClick={() => setSelectedBadge(item.badge)}
                      >
                        <img src={item.badge.smallImageUrl} alt={item.badge.name} className="badge-comparison-image" />
                        <span className="badge-name">{item.badge.name}</span>
                        <div className="ownership-indicators">
                          {item.profile1Has && <span className="indicator profile1">1</span>}
                          {item.profile2Has && <span className="indicator profile2">2</span>}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="stat-card">
        <h2>Group Winners</h2>
        <div className="group-winners-list">
          {groupWinners.map(({ groupName, winners, colors }) => {
            const group = BADGE_GROUPS.find((g) => g.name === groupName);

            // Calculate total possible points for this group
            const totalGroupPoints = group
              ? group.badgesIDs.reduce((sum, badgeID) => {
                  const badge = badgesMap.get(badgeID);
                  return sum + (badge?.rarityScore || 0);
                }, 0)
              : 0;

            return (
              <div
                key={groupName}
                className="group-winner-item"
                data-group-tint=""
                style={
                  {
                    '--group-tint': `linear-gradient(125deg, ${colors[0]}14, ${colors[1]}1c)`,
                    '--group-tint-border': `${colors[0]}40`,
                  } as React.CSSProperties
                }
              >
                <span>{groupName}</span>
                {winners.length > 0 ? (
                  <div className="group-winners-container">
                    {winners.map((winner, index) => {
                      // Calculate points for this winner in this group
                      const winnerPoints = group
                        ? winner.badgesIDs
                            .filter((id) => group.badgesIDs.includes(id))
                            .reduce((sum, badgeID) => {
                              const badge = badgesMap.get(badgeID);
                              return sum + (badge?.rarityScore || 0);
                            }, 0)
                        : 0;

                      return (
                        <ParticipantCard
                          key={winner.email}
                          profile={winner}
                          badgesMap={badgesMap}
                          onClick={() =>
                            handleProfileClick(winner.email, {
                              section: 'collected',
                              groupSlug: slugifyGroupName(groupName),
                            })
                          }
                          subtext={`${getRankDisplay(index + 1)} with ${winnerPoints}/${totalGroupPoints} points`}
                          variant="stats"
                          showBadgeTooltip={true}
                          badgeTooltipIDs={group?.badgesIDs || []}
                          onBadgeClick={setSelectedBadge}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <span>No progress yet</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="stat-card">
        <h2>All Badges by Claimed Percentage</h2>
        <div className="most-claimed-badges">
          {badgesSortedByClaimedPercentage.map((badge) => {
            // Claimed-By count (matches modal); % may use a smaller since-added sample (*).
            const claimedBy = badge.participantIDsWhoOwnThisBadge?.length ?? 0;
            const percentageLabel = formatBadgeClaimPercent(badge);
            const percentage = badgeClaimPercent(badge);
            return (
              <div
                key={badge.id}
                className="journey-stat-item clickable-stat-item"
                onClick={() => setSelectedBadge(badge)}
              >
                <div className="journey-info">
                  <div className="badge-info-with-image">
                    <img src={badge.smallImageUrl} alt={badge.name} className="badge-stat-image" />
                    <span>{badge.name}</span>
                  </div>
                  <span className="no-wrap">
                    Claimed by {claimedBy} profile{claimedBy === 1 ? '' : 's'} ({percentageLabel})
                  </span>
                </div>
                <div className="bar-container">
                  <div className={`bar rarity-${badge.rarityName}`} style={{ width: `${percentage}%` }}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="stat-card" style={{ display: 'none' }}>
        <h2>Badges Used in Multiple Journeys</h2>
        <div className="most-claimed-badges compact">
          {badgesUsedInMultipleJourneys.map(({ badge, journeyCount }) => (
            <div
              key={badge.id}
              className="journey-stat-item clickable-stat-item"
              onClick={() => setSelectedBadge(badge)}
            >
              <div className="journey-info">
                <div className="badge-info-with-image">
                  <img src={badge.smallImageUrl} alt={badge.name} className="badge-stat-image" />
                  <span>{badge.name}</span>
                </div>
                <span className="no-wrap">
                  {journeyCount} journey{journeyCount > 1 ? 's' : ''}
                </span>
              </div>
              <div className="bar-container">
                <div
                  className={`bar rarity-${badge.rarityName}`}
                  style={{
                    width: `${
                      (journeyCount / Math.max(...badgesUsedInMultipleJourneys.map((b) => b.journeyCount))) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="stat-card">
        <h2>Badges Unused in any Journey</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '8px',
          }}
        >
          {badgesNotInAnyJourney.map((badge) => {
            const percentageLabel = formatBadgeClaimPercent(badge);
            return (
              <div
                key={badge.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                }}
                onClick={() => setSelectedBadge(badge)}
              >
                <img src={badge.smallImageUrl} alt={badge.name} style={{ width: '28px', height: '28px' }} />
                <span
                  style={{
                    flex: 1,
                    fontSize: '0.9em',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {badge.name}
                </span>
                <img
                  src={getRarityImageUrlByName(badge.rarityName)}
                  alt={badge.rarityName}
                  style={{ width: '16px', height: '16px' }}
                />
                <span
                  style={{
                    fontSize: '0.85em',
                    color: '#666',
                    minWidth: '35px',
                    textAlign: 'right',
                  }}
                >
                  {percentageLabel}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
